import { memo, useState, useCallback, useEffect } from 'react'
import type { Program, LiveSource, LogoProfile } from '../../types/index.js'
import type { ProgramCategory } from '../../types/domain.js'
import { useProgramActions } from '../../hooks/usePrograms.js'
import { liveSourcesApi, logoProfilesApi, programCategoriesApi } from '../../lib/api.js'
import { NO_LOGO_VALUE } from '../../lib/utils.js'
import { confirmSuggestedProgramMedia } from '../../lib/program-media-suggestions.js'
import { programLogoProfileId } from '../../lib/program-logo-selection.js'
import { ModalShell } from '../common/ModalShell.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface NewProgramModalProps {
  channelId: string
  onCreated: (p: Program) => void
  onClose:   () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const NewProgramModal = memo(function NewProgramModal({ channelId, onCreated, onClose }: NewProgramModalProps) {
  const { createProgram } = useProgramActions()
  const [visible, setVisible] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const [title,       setTitle]       = useState('')
  const [programCategoryId, setProgramCategoryId] = useState('')
  const [categories,  setCategories]  = useState<ProgramCategory[]>([])
  const [programCode, setProgramCode] = useState('')
  const [partDurationsMin, setPartDurationsMin] = useState(['52'])
  const [interPartBreakMin, setInterPartBreakMin] = useState('0')
  const [genre,       setGenre]       = useState('')
  const [liveSourceId,setLiveSourceId]= useState('')
  const [isFourThree, setIsFourThree] = useState(false)
  const [liveSources, setLiveSources] = useState<LiveSource[]>([])
  const [logoProfiles, setLogoProfiles] = useState<LogoProfile[]>([])
  const [logoSelection, setLogoSelection] = useState({ logoProfileId: '', isLive: false, isNew: true })
  const { logoProfileId, isLive, isNew } = logoSelection
  const setLogoProfileId = useCallback((value: string) => {
    setLogoSelection(current => ({ ...current, logoProfileId: value }))
  }, [])

  // Animation d'entrée
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    Promise.all([liveSourcesApi.list(channelId), logoProfilesApi.list(channelId), programCategoriesApi.list({ channelId })])
      .then(([sources, profiles, cats]) => {
        setLiveSources(sources)
        setLogoProfiles(profiles)
        setLogoSelection(current => ({ ...current, logoProfileId: programLogoProfileId(profiles, current) }))
        setCategories(cats)
      })
      .catch(console.error)
  }, [channelId])

  const changeIsNew = useCallback((value: boolean) => {
    setLogoSelection(current => {
      const next = { ...current, isNew: value }
      return { ...next, logoProfileId: programLogoProfileId(logoProfiles, next, true) }
    })
  }, [logoProfiles])

  const changeIsLive = useCallback((value: boolean) => {
    setLogoSelection(current => {
      const next = { ...current, isLive: value }
      return { ...next, logoProfileId: programLogoProfileId(logoProfiles, next, true) }
    })
  }, [logoProfiles])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 200)
  }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !logoProfileId) return
    setSaving(true)
    setError(null)
    try {
      const program = await createProgram({
        channelId,
        title:      title.trim(),
        type:       'EMISSION',
        programCategoryId: programCategoryId || null,
        programCode: programCode.trim() ? programCode.trim().toUpperCase() : null,
        durationSec: Math.max(1, parseInt(partDurationsMin[0] || '1')) * 60,
        interPartBreakSec: Math.max(0, Number(interPartBreakMin || 0)) * 60,
        additionalParts: isLive ? [] : partDurationsMin.slice(1).map(value => ({
          durationSec: Math.max(1, parseInt(value || '1')) * 60,
        })),
        genre:       genre.trim() || undefined,
        // « Aucun logo » ne désigne aucun profil : le serveur retiendra celui
        // par défaut, que `noLogo` neutralise à l'antenne.
        logoProfileId: logoProfileId === NO_LOGO_VALUE ? undefined : logoProfileId,
        noLogo:      logoProfileId === NO_LOGO_VALUE,
        isLive,
        liveSourceId: isLive && liveSourceId ? liveSourceId : undefined,
        isFourThree:  !isLive && isFourThree,
        isNew,
        tags:        [],
      })
      const linkedProgram = await confirmSuggestedProgramMedia(program.id).catch(() => null)
      onCreated(linkedProgram ?? program)
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }, [channelId, title, programCategoryId, programCode, partDurationsMin, interPartBreakMin, genre, logoProfileId, isLive, liveSourceId, isFourThree, isNew, createProgram, onCreated])

  return (
    <ModalShell title="Nouveau programme" visible={visible} onClose={handleClose}>
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && (
            <div className="tp-feedback tp-feedback--error" role="alert">
              {error}
            </div>
          )}

          <FormField label="Titre *">
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Titre du programme"
              required
              autoFocus
              className="tp-input"
            />
          </FormField>

          <div className="tp-form-grid">
            <FormField label="Type">
              <select value={programCategoryId} onChange={e => setProgramCategoryId(e.target.value)} className="tp-select">
                <option value="">— Aucun type —</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Durée (minutes)">
              <input
                type="number"
                value={partDurationsMin[0]}
                onChange={e => setPartDurationsMin(parts => [e.target.value, ...parts.slice(1)])}
                min="1"
                className="tp-input"
              />
            </FormField>
          </div>

          {!isLive && (
            <div className="tp-form-panel">
              <div className="tp-form-grid">
                <FormField label="Nombre de parties">
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={partDurationsMin.length}
                    onChange={e => {
                      const count = Math.min(20, Math.max(1, Number(e.target.value)))
                      setPartDurationsMin(parts => Array.from({ length: count }, (_, index) => parts[index] ?? parts[0] ?? '52'))
                    }}
                    className="tp-input"
                  />
                </FormField>
                <FormField label="Coupure entre parties (minutes)">
                  <input type="number" min="0" step="0.5" value={interPartBreakMin} onChange={e => setInterPartBreakMin(e.target.value)} className="tp-input" />
                </FormField>
              </div>
              {partDurationsMin.length > 1 && (
                <div className="tp-form-grid tp-form-grid--three catalog-part-grid">
                  {partDurationsMin.map((value, index) => (
                    <FormField key={index} label={`Partie ${index + 1} (min)`}>
                      <input
                        type="number"
                        min="1"
                        value={value}
                        onChange={e => setPartDurationsMin(parts => parts.map((part, partIndex) => partIndex === index ? e.target.value : part))}
                        className="tp-input"
                      />
                    </FormField>
                  ))}
                </div>
              )}
            </div>
          )}

          <FormField label="Genre">
            <input
              value={genre}
              onChange={e => setGenre(e.target.value)}
              placeholder="Documentaire, Émission, Sport…"
              className="tp-input"
            />
          </FormField>

          <FormField label="Code programme">
            <input
              value={programCode}
              onChange={e => setProgramCode(e.target.value.toUpperCase())}
              placeholder="EX: JTLOCAL"
              className="tp-input tp-input--data"
            />
          </FormField>

          <FormField label="Habillage logo">
            <select value={logoProfileId} onChange={e => setLogoProfileId(e.target.value)} disabled={!logoProfiles.length} required className="tp-select">
              {!logoProfileId && <option value="" disabled>Aucun profil automatique disponible</option>}
              {logoProfiles.filter(profile => profile.active).map(profile => (
                <option key={profile.id} value={profile.id}>{profile.name}</option>
              ))}
              <option value={NO_LOGO_VALUE}>Aucun logo (le fichier en porte déjà un)</option>
            </select>
          </FormField>

          <label className="tp-check-row" htmlFor="isLive">
            <input
              type="checkbox"
              id="isLive"
              checked={isLive}
              onChange={e => changeIsLive(e.target.checked)}
            />
            <span>
              Programme direct (live) — aucun fichier média requis
            </span>
          </label>

          {isLive && (
            <FormField label="Source live par défaut">
              <select
                value={liveSourceId}
                onChange={e => setLiveSourceId(e.target.value)}
                className="tp-select"
              >
                <option value="">Aucune (sélection manuelle dans la grille)</option>
                {liveSources.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </FormField>
          )}

          {!isLive && (
            <FormField label="Format image">
              <AspectRatioSelector isFourThree={isFourThree} onChange={setIsFourThree} />
            </FormField>
          )}

          <label className="tp-check-row" htmlFor="isNew">
            <input
              type="checkbox"
              id="isNew"
              checked={isNew}
              onChange={e => changeIsNew(e.target.checked)}
            />
            <span>
              Inédit lors de la prochaine diffusion — décochez pour un best-of ou un contenu déjà diffusé
            </span>
          </label>

          {/* Actions */}
          <div className="tp-modal-actions">
            <button
              type="button"
              onClick={handleClose}
              className="tp-button"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim() || !logoProfileId}
              className="tp-button tp-button--info"
              data-state={saving ? 'loading' : undefined}
            >
              {saving ? 'Création…' : 'Créer le programme'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="tp-form-field">
      <span className="tp-field-label">{label}</span>
      {children}
    </label>
  )
}

function AspectRatioSelector({ isFourThree, onChange }: { isFourThree: boolean; onChange: (value: boolean) => void }) {
  const options = [
    { label: '16/9', description: 'Plein cadre', value: false },
    { label: '4/3', description: 'Pillarbox', value: true },
  ]
  return (
    <div className="catalog-aspect-options">
      {options.map(option => {
        const active = isFourThree === option.value
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onChange(option.value)}
            className={`catalog-aspect-option${active ? ' is-active' : ''}`}
            aria-pressed={active}
          >
            <span className="catalog-aspect-option__title">{option.label}</span>
            <span className="catalog-aspect-option__description">{option.description}</span>
          </button>
        )
      })}
    </div>
  )
}
